/**
 * Course category New-badge service.
 * Default: live editor API. Set REACT_APP_USE_CATEGORY_NEW_MOCK=true to use local mock.
 */
import api from '../api/index.api'
import {
	getCategoryDisplayName,
	getParentChipPreview as mockGetParentChipPreview,
	listCourseCategoryLeaves as mockListLeaves,
	resetCourseCategoryNewSeed as mockReset,
	setCourseCategoryIsNew as mockSetIsNew,
	sortSubcategoriesForClient,
} from './courseCategoryNewMock.store'

const isMockEnabled = () =>
	String(process.env.REACT_APP_USE_CATEGORY_NEW_MOCK || '').toLowerCase() ===
	'true'

const nameOf = (translations, locale) =>
	translations?.[locale]?.name ||
	translations?.en?.name ||
	translations?.vi?.name ||
	'—'

const normalizeTree = roots => {
	if (!Array.isArray(roots)) return []
	return roots.map(root => ({
		id: Number(root.id),
		parentId: root.parentID ?? root.parentId ?? null,
		translations: root.translations || {},
		isNew: Boolean(root.isNew),
		updatedAt: root.updatedAt || null,
		subCategories: (root.subCategories || []).map(sub => ({
			id: Number(sub.id),
			parentId: sub.parentID ?? sub.parentId ?? Number(root.id),
			translations: sub.translations || {},
			isNew: Boolean(sub.isNew),
			updatedAt: sub.updatedAt || null,
		})),
	}))
}

let treeCache = null
let treeCacheAt = 0
const CACHE_TTL_MS = 5_000

const invalidateTreeCache = () => {
	treeCache = null
	treeCacheAt = 0
}

const loadTree = async ({ force = false } = {}) => {
	const now = Date.now()
	if (!force && treeCache && now - treeCacheAt < CACHE_TTL_MS) {
		return treeCache
	}
	const raw = await api.courseCategoryAdminApi.getAll()
	treeCache = normalizeTree(raw)
	treeCacheAt = now
	return treeCache
}

const flattenLeaves = roots => {
	const leaves = []
	for (const root of roots) {
		for (const sub of root.subCategories || []) {
			leaves.push({
				...sub,
				parentNameEn: nameOf(root.translations, 'en'),
				parentNameVi: nameOf(root.translations, 'vi'),
			})
		}
	}
	return leaves
}

/**
 * @param {{
 *   q?: string,
 *   parentId?: number,
 *   isNew?: boolean,
 *   page?: number,
 *   pageSize?: number,
 * }} filters
 */
export async function listCourseCategoryLeaves(filters = {}) {
	if (isMockEnabled()) {
		return mockListLeaves(filters)
	}

	const {
		q,
		parentId,
		isNew,
		page = 0,
		pageSize = 50,
	} = filters
	const query = String(q || '')
		.trim()
		.toLowerCase()

	const roots = await loadTree()
	const leaves = flattenLeaves(roots)
	let items = leaves.slice()

	if (parentId != null && parentId !== '') {
		items = items.filter(item => Number(item.parentId) === Number(parentId))
	}
	if (typeof isNew === 'boolean') {
		items = items.filter(item => Boolean(item.isNew) === isNew)
	}
	if (query) {
		items = items.filter(item => {
			const hay = [
				item.id,
				item.parentNameEn,
				item.parentNameVi,
				item.translations?.en?.name,
				item.translations?.vi?.name,
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
			return hay.includes(query)
		})
	}

	items.sort((a, b) => {
		if (Boolean(a.isNew) !== Boolean(b.isNew)) {
			return a.isNew ? -1 : 1
		}
		const parentCmp = String(a.parentNameEn || '').localeCompare(
			String(b.parentNameEn || '')
		)
		if (parentCmp !== 0) return parentCmp
		return nameOf(a.translations, 'en').localeCompare(
			nameOf(b.translations, 'en')
		)
	})

	const total = items.length
	const newCount = leaves.filter(item => item.isNew).length
	const start = page * pageSize
	const data = items.slice(start, start + pageSize)

	const parents = roots.map(root => ({
		id: root.id,
		nameEn: nameOf(root.translations, 'en'),
		nameVi: nameOf(root.translations, 'vi'),
		subCount: (root.subCategories || []).length,
		newCount: (root.subCategories || []).filter(s => s.isNew).length,
	}))

	return { data, total, newCount, parents }
}

export async function getParentChipPreview(parentId) {
	if (isMockEnabled()) {
		return mockGetParentChipPreview(parentId)
	}

	const roots = await loadTree()
	const parent = roots.find(root => Number(root.id) === Number(parentId))
	if (!parent) {
		const err = new Error('Parent category not found.')
		err.code = 'NOT_FOUND'
		throw err
	}
	const ordered = sortSubcategoriesForClient(parent.subCategories || [])
	return {
		parent: {
			id: parent.id,
			nameEn: nameOf(parent.translations, 'en'),
			nameVi: nameOf(parent.translations, 'vi'),
		},
		chips: ordered.map(sub => ({
			id: sub.id,
			nameEn: nameOf(sub.translations, 'en'),
			nameVi: nameOf(sub.translations, 'vi'),
			isNew: Boolean(sub.isNew),
		})),
	}
}

export async function setCourseCategoryIsNew(id, isNew) {
	if (isMockEnabled()) {
		return mockSetIsNew(id, isNew)
	}

	const updated = await api.courseCategoryAdminApi.setIsNew(id, isNew)
	invalidateTreeCache()
	// Refresh tree so subsequent list/preview are consistent
	await loadTree({ force: true })
	return {
		id: Number(updated?.id ?? id),
		isNew: Boolean(updated?.isNew ?? isNew),
		parentId: updated?.parentID ?? updated?.parentId,
		translations: updated?.translations || {},
		updatedAt: updated?.updatedAt || new Date().toISOString(),
		parentNameEn: nameOf(updated?.translations, 'en'),
		parentNameVi: nameOf(updated?.translations, 'vi'),
	}
}

export async function resetCourseCategoryNewSeed() {
	if (isMockEnabled()) {
		return mockReset()
	}
	const err = new Error(
		'Reset seed is only available in mock mode (REACT_APP_USE_CATEGORY_NEW_MOCK=true).'
	)
	err.code = 'NOT_SUPPORTED'
	throw err
}

export { getCategoryDisplayName }
